"use client";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import AppContext from "@/lib/app-context";
import type { AwsRum } from "aws-rum-web";
import { getPublicEnv } from '@/utils/env';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export default function AppProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [awsRum, setAwsRum] = useState<AwsRum | undefined>(undefined);

  // Lazy load AWS RUM only on client-side, after mount
  useEffect(() => {
    // Only load when RUM is explicitly enabled and configured
    if (
      typeof window !== 'undefined' &&
      getPublicEnv('NEXT_PUBLIC_ENABLE_RUM') === 'true' &&
      window.location.hostname !== 'localhost' &&
      getPublicEnv('NEXT_PUBLIC_RUM_IDENTITY_POOL_ARN')
    ) {
      import('@/lib/rum').then(({ bootstrapRum }) => {
        const rum = bootstrapRum();
        setAwsRum(rum);
      }).catch((error) => {
        console.error('Failed to load AWS RUM:', error);
      });
    }
  }, []);

  const value = useMemo(() => ({ awsRum }), [awsRum]);

  return (
    <AppContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </AppContext.Provider>
  );
}
